<template>
  <article class="invoice-create-card payment-receipt-application-card">
    <header class="invoice-create-card__head invoice-create-card__head--space">
      <h3>
        <i class="fa fa-link invoice-create-section-icon invoice-create-section-icon--contacts" aria-hidden="true"></i>
        {{ text?.create?.ui?.applicationCard?.title || 'Aplicación del pago' }}
      </h3>
    </header>

    <div v-if="loading" class="payment-receipt-application-card__loading">
      {{ text?.create?.ui?.applicationCard?.loading || 'Cargando elementos pendientes...' }}
    </div>

    <div v-else class="app-table-wrap invoice-create-concepts-table-wrap payment-receipt-application-card__table-wrap">
      <div class="app-table-scroll">
        <table class="app-data-table payment-receipt-application-card__data-table">
          <colgroup>
            <col class="payment-receipt-col payment-receipt-col--date" />
            <col class="payment-receipt-col payment-receipt-col--document" />
            <col class="payment-receipt-col payment-receipt-col--category" />
            <col class="payment-receipt-col payment-receipt-col--business" />
            <col class="payment-receipt-col payment-receipt-col--concept" />
            <col class="payment-receipt-col payment-receipt-col--total" />
            <col class="payment-receipt-col payment-receipt-col--pending" />
            <col class="payment-receipt-col payment-receipt-col--applied" />
            <col class="payment-receipt-col payment-receipt-col--liquidate" />
          </colgroup>
          <thead>
            <tr>
              <th class="payment-receipt-col-fixed">{{ text?.create?.ui?.applicationCard?.table?.headers?.date || 'FECHA' }}</th>
              <th class="payment-receipt-col-fixed">{{ text?.create?.ui?.applicationCard?.table?.headers?.folio || 'FOLIO' }}</th>
              <th>{{ text?.create?.ui?.applicationCard?.table?.headers?.category || 'CATEGORIA' }}</th>
              <th>{{ text?.create?.ui?.applicationCard?.table?.headers?.business || 'NEGOCIO' }}</th>
              <th>{{ text?.create?.ui?.applicationCard?.table?.headers?.concept || 'CONCEPTO' }}</th>
              <th class="app-text-right payment-receipt-col-fixed">{{ text?.create?.ui?.applicationCard?.table?.headers?.total || 'TOTAL' }}</th>
              <th class="app-text-right payment-receipt-col-fixed">{{ text?.create?.ui?.applicationCard?.table?.headers?.pending || 'SALDO PENDIENTE' }}</th>
              <th class="app-text-right payment-receipt-col-fixed">{{ text?.create?.ui?.applicationCard?.table?.headers?.applied || 'MONTO A PAGAR' }}</th>
              <th class="app-text-center payment-receipt-col-fixed">{{ text?.create?.ui?.applicationCard?.table?.headers?.liquidate || 'APLICAR TODO' }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="documents.length === 0">
              <td :colspan="9" class="app-table-empty">{{ emptyStateMessage }}</td>
            </tr>

            <template v-for="row in documents" :key="row.id">
              <tr>
                <td class="payment-receipt-col-fixed">{{ formatDate(row.date) }}</td>
                <td class="payment-receipt-col-fixed">
                  <a
                    v-if="row.invoiceId > 0 && resolveExpenseFolio(row) !== ''"
                    :href="resolveInvoiceHref(row)"
                    class="expenses-table-link payment-receipt-application-card__doc-link"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {{ resolveExpenseFolio(row) }}
                  </a>
                  <strong v-else>{{ resolveExpenseFolio(row) }}</strong>
                </td>
                <td>
                  <div class="expenses-cell-stack">
                    <strong class="expenses-cell-main">{{ resolveExpenseClassName(row) }}</strong>
                    <span class="expenses-cell-sub">{{ resolveCategoryName(row) }}</span>
                  </div>
                </td>
                <td>
                  <div v-if="hasBusinessRelation(row)" class="expenses-cell-stack">
                    <a
                      v-if="row.customerId > 0"
                      :href="resolveCustomerHref(row)"
                      class="expenses-table-link expenses-cell-main"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {{ resolveCustomerName(row) }}
                    </a>
                    <span v-else class="expenses-cell-main">{{ resolveCustomerName(row) }}</span>

                    <a
                      v-if="row.saleId > 0"
                      :href="resolveSaleHref(row)"
                      class="expenses-table-link expenses-cell-sub"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {{ resolveSaleDisplayName(row) }}
                    </a>
                    <span v-else class="expenses-cell-sub">{{ resolveSaleDisplayName(row) }}</span>
                  </div>
                  <span v-else></span>
                </td>
                <td>
                  <div class="payment-receipt-application-card__concept-cell">
                    <span>{{ resolvePrimaryConcept(row) }}</span>
                    <button
                      v-if="hasExtraConcepts(row)"
                      type="button"
                      class="payment-receipt-application-card__concept-toggle"
                      @click="toggleConcepts(row)"
                    >
                      <i class="fa" :class="isConceptsExpanded(row) ? 'fa-angle-up' : 'fa-angle-down'" aria-hidden="true"></i>
                    </button>
                  </div>
                </td>
                <td class="app-text-right app-strong payment-receipt-col-fixed">
                  <span class="amount-cell-total">{{ formatMoney(row.total) }}</span>
                </td>
                <td class="app-text-right app-strong payment-receipt-col-fixed">
                  <div class="payment-receipt-pending-op">
                    <strong>{{ formatMoney(row.pendingAmount) }}</strong>
                    <small v-if="resolveAppliedAmount(row) > 0" class="payment-receipt-pending-op__subtract">
                      - {{ formatMoney(resolveAppliedAmount(row)) }}
                    </small>
                    <small v-if="resolveAppliedAmount(row) > 0" class="payment-receipt-pending-op__result">
                      = {{ formatMoney(resolvePendingAfterApplied(row)) }}
                    </small>
                  </div>
                </td>
                <td class="payment-receipt-col-fixed">
                  <AppMoneyInput
                    :model-value="row.appliedAmount"
                    output-mode="number"
                    :decimal-places="2"
                    :minimum-value="0"
                    :maximum-value="row.pendingAmount"
                    :currency="currency"
                    :disabled="isAppliedAmountInputDisabled(row)"
                    @update:model-value="onAppliedAmountChange(row, $event)"
                  />
                </td>
                <td class="app-text-center payment-receipt-col-fixed payment-receipt-col-liquidate-cell">
                  <button
                    type="button"
                    class="payment-receipt-liquidate-toggle"
                    :class="{ 'payment-receipt-liquidate-toggle--on': isRowLiquidated(row) }"
                    :disabled="isLiquidateToggleDisabled(row)"
                    @click="toggleLiquidate(row)"
                  >
                    <span class="payment-receipt-liquidate-toggle__knob"></span>
                  </button>
                </td>
              </tr>

              <tr v-if="isConceptsExpanded(row)">
                <td :colspan="conceptPanelLeadingColspan"></td>
                <td :colspan="conceptPanelMainColspan" class="payment-receipt-application-card__concepts-panel-cell">
                  <div class="payment-receipt-application-card__concepts-wrap payment-receipt-application-card__concepts-wrap--compact">
                    <header class="payment-receipt-application-card__concepts-head">
                      <span>Conceptos de esta factura</span>
                    </header>

                    <div class="payment-receipt-application-card__concepts-list">
                      <div class="payment-receipt-application-card__concept-item payment-receipt-application-card__concept-item--head">
                        <div>CONCEPTO</div>
                        <div class="app-text-right">SUBTOTAL</div>
                        <div class="app-text-right">IMPUESTOS</div>
                        <div class="app-text-right">TOTAL</div>
                      </div>

                      <div
                        v-for="(concept, index) in resolveConcepts(row)"
                        :key="`${row.id}-concept-${index}`"
                        class="payment-receipt-application-card__concept-item"
                      >
                        <div class="payment-receipt-application-card__concept-item-description">
                          {{ concept.description || '-' }}
                        </div>
                        <div class="payment-receipt-application-card__concept-item-value app-text-right">
                          {{ formatMoney(concept.subtotal) }}
                        </div>
                        <div class="payment-receipt-application-card__concept-item-value app-text-right">
                          {{ formatMoney(concept.taxes) }}
                        </div>
                        <div class="payment-receipt-application-card__concept-item-total app-text-right">
                          {{ formatMoney(concept.total) }}
                        </div>
                      </div>
                    </div>
                  </div>
                </td>
                <td :colspan="conceptPanelTrailingColspan"></td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>
  </article>
</template>

<script>
import AppMoneyInput from '@/components/common/AppMoneyInput.vue';
import { resolveRouteHref } from '@/composables/shared/useNavigation';
import { formatDateShort } from '@/utils/datePresentation';
import { formatCurrency } from '@/utils/numberFormat';
import { formatCodeName } from '@/utils/textValue';

export default {
  name: 'ExpenseReceiptCreateApplicationSection',
  components: {
    AppMoneyInput,
  },
  props: {
    text: {
      type: Object,
      default: () => ({}),
    },
    documents: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    voucherType: {
      type: String,
      default: 'cfdi',
    },
    currency: {
      type: String,
      default: 'MXN',
    },
    receivedAmount: {
      type: Number,
      default: 0,
    },
  },
  emits: ['update-application', 'toggle-liquidate'],
  data() {
    return {
      expandedConcepts: {},
    };
  },
  computed: {
    isCfdiMode() {
      return String(this.voucherType || 'cfdi').trim().toLowerCase() !== 'simple_note';
    },
    emptyStateMessage() {
      const normalized = String(this.voucherType || '').trim().toLowerCase();
      if (normalized === 'simple_note') {
        return this.text?.create?.ui?.applicationCard?.emptySimpleNote
          || 'No hay notas simples pendientes o parcialmente pagadas para el proveedor seleccionado.';
      }

      return this.text?.create?.ui?.applicationCard?.emptyCfdi
        || 'No hay CFDI pendientes o parcialmente pagados para el proveedor seleccionado.';
    },
    canToggleLiquidate() {
      return Number(this.receivedAmount || 0) > 0;
    },
    conceptPanelLeadingColspan() {
      // Keep Fecha + Folio fixed and start the breakdown from Categoria.
      return 2;
    },
    conceptPanelMainColspan() {
      // Categoria + Negocio + Concepto columns.
      return 3;
    },
    conceptPanelTrailingColspan() {
      // Pending + Applied + Liquidate columns remain outside the breakdown panel.
      return 3;
    },
    remainingAmountAvailable() {
      const receivedAmount = Math.max(0, Number(this.receivedAmount || 0) || 0);
      const allocatedAmount = (Array.isArray(this.documents) ? this.documents : [])
        .reduce((accumulator, row) => accumulator + this.resolveAppliedAmount(row), 0);

      return Math.max(0, receivedAmount - allocatedAmount);
    },
    hasAnyLiquidatedRow() {
      return (Array.isArray(this.documents) ? this.documents : [])
        .some((row) => this.isRowLiquidated(row));
    },
  },
  methods: {
    formatDate(value) {
      return formatDateShort(value) || '-';
    },
    formatMoney(amount) {
      return formatCurrency(amount, { currency: this.currency });
    },
    resolveInvoiceHref(row) {
      return resolveRouteHref(this.$router, 'invoiceDetail', 'invoiceId', Number(row?.invoiceId || 0));
    },
    resolveCustomerHref(row) {
      return resolveRouteHref(this.$router, 'customerDetail', 'customerId', Number(row?.customerId || 0));
    },
    resolveSaleHref(row) {
      return resolveRouteHref(this.$router, 'saleDetail', 'saleId', Number(row?.saleId || 0));
    },
    resolveExpenseClassName(row) {
      return String(row?.expenseClass || row?.expense_class || '').trim() || '-';
    },
    resolveCategoryName(row) {
      return String(row?.category || '').trim() || '-';
    },
    resolveCustomerName(row) {
      return String(row?.customerName || row?.customer_name || '').trim();
    },
    resolveSaleDisplayName(row) {
      return formatCodeName(
        row?.saleCode || row?.sale_code,
        row?.businessName || row?.business_name || row?.saleName || row?.sale_name,
      );
    },
    hasBusinessRelation(row) {
      return this.resolveCustomerName(row) !== '' || this.resolveSaleDisplayName(row) !== '';
    },
    resolveExpenseFolio(row) {
      return String(row?.folio || row?.reference || '').trim();
    },
    resolvePrimaryConcept(row) {
      const concepts = this.resolveConcepts(row);
      if (concepts.length === 0) {
        return String(row?.concept || '-').trim() || '-';
      }

      return String(concepts[0]?.description || row?.concept || '-').trim() || '-';
    },
    resolveConcepts(row) {
      return Array.isArray(row?.concepts) ? row.concepts : [];
    },
    hasExtraConcepts(row) {
      return this.resolveConcepts(row).length > 1;
    },
    isConceptsExpanded(row) {
      const key = String(row?.id || '');
      return Boolean(this.expandedConcepts[key]);
    },
    toggleConcepts(row) {
      const key = String(row?.id || '');
      if (key === '') {
        return;
      }

      this.expandedConcepts = {
        ...this.expandedConcepts,
        [key]: !this.expandedConcepts[key],
      };
    },
    onAppliedAmountChange(row, amount) {
      this.$emit('update-application', {
        id: row?.id,
        amount: Number(amount || 0) || 0,
      });
    },
    resolveAppliedAmount(row) {
      return Math.max(0, Number(row?.appliedAmount || 0) || 0);
    },
    resolvePendingAfterApplied(row) {
      const pendingAmount = Math.max(0, Number(row?.pendingAmount || 0) || 0);
      return Math.max(0, pendingAmount - this.resolveAppliedAmount(row));
    },
    isRowLiquidated(row) {
      return Number(row?.isLiquidated ? 1 : 0) === 1;
    },
    isAppliedAmountInputDisabled(row) {
      if (this.isRowLiquidated(row)) {
        return true;
      }

      if (Number(this.receivedAmount || 0) <= 0) {
        return true;
      }

      if (this.hasAnyLiquidatedRow && this.remainingAmountAvailable <= 0) {
        return true;
      }

      if (this.remainingAmountAvailable <= 0 && this.resolveAppliedAmount(row) <= 0) {
        return true;
      }

      return false;
    },
    isLiquidateToggleDisabled(_row) {
      if (!this.canToggleLiquidate) {
        return true;
      }

      if (this.isRowLiquidated(_row)) {
        return false;
      }

      return this.remainingAmountAvailable <= 0;
    },
    toggleLiquidate(row) {
      this.$emit('toggle-liquidate', {
        id: row?.id,
        value: !this.isRowLiquidated(row),
      });
    },
  },
};
</script>

<style scoped>
.payment-receipt-application-card {
  display: grid;
  gap: 10px;
}

.payment-receipt-application-card__loading,
.payment-receipt-application-card__empty {
  border: 1px dashed rgba(148, 163, 184, 0.45);
  border-radius: 8px;
  min-height: 84px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  font-size: 13px;
  text-align: center;
  padding: 16px;
}

.payment-receipt-application-card__table-wrap {
  min-width: 100%;
}

.payment-receipt-application-card__data-table {
  table-layout: fixed;
  width: 100%;
}

.payment-receipt-col-fixed {
  white-space: nowrap;
}

.payment-receipt-col--document {
  width: 130px;
}

.payment-receipt-col--date {
  width: 110px;
}

.payment-receipt-col--category {
  width: 160px;
}

.payment-receipt-col--business,
.payment-receipt-col--concept {
  width: auto;
}

.expenses-cell-stack {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.expenses-cell-main {
  font-size: 13px;
  line-height: 1.3;
  font-weight: 700;
  color: #0f172a;
  min-width: 0;
  overflow-wrap: break-word;
}

.expenses-cell-sub {
  font-size: 11px;
  line-height: 1.3;
  color: #6b7280;
  min-width: 0;
  overflow-wrap: break-word;
}

.payment-receipt-col--total {
  width: 130px;
}

.payment-receipt-col--pending {
  width: 150px;
}

.payment-receipt-col--applied {
  width: 170px;
}

.payment-receipt-col--liquidate {
  width: 112px;
}

.payment-receipt-col-liquidate-cell {
  text-align: center;
}

.payment-receipt-application-card__doc-link {
  font-weight: 700;
  color: #0070cd;
}

.payment-receipt-application-card__concept-cell {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.payment-receipt-application-card__concept-toggle {
  border: 0;
  background: transparent;
  color: #0070cd;
  padding: 0;
  line-height: 1;
}

.payment-receipt-application-card__concepts-panel-cell {
  background: transparent;
  padding: 8px 8px 10px;
}

.payment-receipt-application-card__concepts-wrap {
  border: 1px solid rgba(148, 163, 184, 0.35);
  border-radius: 8px;
}

.payment-receipt-application-card__concepts-wrap--compact {
  background: rgba(241, 245, 249, 0.72);
  max-width: 100%;
}

.payment-receipt-application-card__concepts-head {
  padding: 8px 10px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.25);
}

.payment-receipt-application-card__concepts-head span {
  font-size: 11px;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
}

.payment-receipt-application-card__concepts-list {
  display: grid;
  gap: 0;
}

.payment-receipt-application-card__concept-item {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 120px 120px 120px;
  gap: 12px;
  align-items: center;
  padding: 8px 10px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.2);
}

.payment-receipt-application-card__concept-item--head {
  background: rgba(148, 163, 184, 0.12);
}

.payment-receipt-application-card__concept-item--head > div {
  font-size: 11px;
  font-weight: 700;
  color: #475569;
}

.payment-receipt-application-card__concept-item:last-child {
  border-bottom: 0;
}

.payment-receipt-application-card__concept-item-description {
  color: #334155;
  font-size: 12px;
  min-width: 0;
  overflow-wrap: break-word;
  word-break: normal;
  line-height: 1.35;
}

.payment-receipt-application-card__concept-item-total {
  color: #0f172a;
  font-size: 12px;
  font-weight: 700;
}

.payment-receipt-application-card__concept-item-value {
  color: #475569;
  font-size: 12px;
  font-weight: 600;
}

.payment-receipt-pending-op {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 1px;
}

.payment-receipt-pending-op__subtract {
  color: #b45309;
  font-size: 11px;
  font-weight: 600;
}

.payment-receipt-pending-op__result {
  color: #64748b;
  font-size: 11px;
  font-weight: 700;
}

.payment-receipt-liquidate-toggle {
  width: 42px;
  height: 24px;
  display: block;
  margin: 0 auto;
  border: 1px solid rgba(148, 163, 184, 0.5);
  border-radius: 999px;
  background: #e2e8f0;
  position: relative;
  transition: all 0.2s ease;
}

.payment-receipt-liquidate-toggle:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.payment-receipt-liquidate-toggle__knob {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 18px;
  height: 18px;
  border-radius: 999px;
  background: #ffffff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.2);
  transition: transform 0.2s ease;
}

.payment-receipt-liquidate-toggle--on {
  border-color: rgba(0, 112, 205, 0.75);
  background: rgba(0, 112, 205, 0.85);
}

.payment-receipt-liquidate-toggle--on .payment-receipt-liquidate-toggle__knob {
  transform: translateX(18px);
}
</style>
